import { useSyncExternalStore } from 'react'

/**
 * Phone layout (design 36): narrower than 640px, or a touch screen shorter than
 * 500px, so a phone turned sideways keeps the phone layout instead of getting
 * the desktop sidebar with 390px of height. The `phone:` / `desk:` Tailwind
 * variants in styles.css use the same query; keep them in step.
 */
export const PHONE_MEDIA_QUERY =
  '(max-width: 639.98px), (pointer: coarse) and (max-height: 499.98px)'

const TABLET_BREAKPOINT = 1280
// Below this width a percentage-based split view leaves the list pane too narrow
// to render a task row without truncating the title (MOL-11).
const COMPACT_DESKTOP_BREAKPOINT = 1600
// Below this width the docked sidebar costs too much of the page: the sidebar
// stays a rail and its peek works as a drawer (design 5a).
const NARROW_DESKTOP_BREAKPOINT = 1100

export const isPhoneViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(PHONE_MEDIA_QUERY).matches

/** Not a phone, and narrower than `maxWidth`. */
const isWiderThanPhoneBelow = (maxWidth: number) =>
  typeof window !== 'undefined' &&
  !isPhoneViewport() &&
  window.innerWidth < maxWidth

// Resize also fires on rotation, which is what flips the height rule.
const subscribe = (onChange: () => void) => {
  window.addEventListener('resize', onChange)
  return () => window.removeEventListener('resize', onChange)
}

const serverFalse = () => false

export function useIsMobile() {
  return useSyncExternalStore(subscribe, isPhoneViewport, serverFalse)
}

/** Primary pointer is a finger. True on a phone or tablet, false for a mouse. */
export const COARSE_POINTER_QUERY = '(pointer: coarse)'

export const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(COARSE_POINTER_QUERY).matches

export function useIsCoarsePointer() {
  return useSyncExternalStore(subscribe, isCoarsePointer, serverFalse)
}

/**
 * Pickers open as a bottom sheet: a phone-sized window, or any coarse pointer
 * (a tablet is wider than 640px and taller than 500px, so it is not a phone,
 * but it still has an on-screen keyboard). A mouse stays on the popover.
 */
export function useIsTouchPicker() {
  return useIsMobile() || useIsCoarsePointer()
}

const getIsTablet = () => isWiderThanPhoneBelow(TABLET_BREAKPOINT)

export function useIsTablet() {
  return useSyncExternalStore(subscribe, getIsTablet, serverFalse)
}

const getIsCompactDesktop = () => isWiderThanPhoneBelow(COMPACT_DESKTOP_BREAKPOINT)

/**
 * True for laptop-sized windows that are wide enough for the split view but too
 * narrow for it to divide space the way a large desktop display can.
 */
export function useIsCompactDesktop() {
  return useSyncExternalStore(subscribe, getIsCompactDesktop, serverFalse)
}

const getIsNarrowDesktop = () => isWiderThanPhoneBelow(NARROW_DESKTOP_BREAKPOINT)

export function useIsNarrowDesktop() {
  return useSyncExternalStore(subscribe, getIsNarrowDesktop, serverFalse)
}
